import { computed, type ComputedRef } from 'vue'
import { resolveLocalePreference } from '@architeia/utils'
import { KORA_WEB_FALLBACK_LOCALE, KORA_WEB_LOCALES, type KoraWebLocale } from '@/i18n'
import { useAccountPreferencesStore } from '@/stores/accountPreferences'

// navigator.languages is not reactive: a change of browser language is picked up on the next
// reload, not live, so this computed never re-runs for it.
function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return []
  return navigator.languages?.length ? navigator.languages : [navigator.language]
}

/**
 * ADR-010 D3: the single derivation of the language Kora Web shows: the account language (or the
 * per-origin hint until it loads), else the browser's, else English. Derived, never stored;
 * `useLocaleSync` is the only thing that applies it. The account setting is the control, so there
 * is no product override.
 */
export function useEffectiveLocale(): {
  effectiveLocale: ComputedRef<KoraWebLocale>
  /** The account time zone, or null to use the device's. */
  effectiveTimeZone: ComputedRef<string | null>
} {
  const preferences = useAccountPreferencesStore()
  const effectiveLocale = computed(() =>
    resolveLocalePreference(KORA_WEB_LOCALES, KORA_WEB_FALLBACK_LOCALE, {
      accountLocale: preferences.locale,
      browserLanguages: browserLanguages()
    })
  )
  const effectiveTimeZone = computed(() => preferences.timeZone)
  return { effectiveLocale, effectiveTimeZone }
}
